import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useAuth } from "../../../context/AuthContext";
import { canVerTodasLojasUser } from "../../../lib/screenAccess";
import { lojaService } from "../../../services/lojaService";
import { Loja } from "../../../types/loja";
import {
  useCreateEntrega,
  useRankingDiaEntrega,
  useUpdateFilaEntregaStatus,
} from "../../../hooks/useRoterista";
import { useLojaById } from "../../../hooks/useLojas";
import { useRankingEvents } from "../../../hooks/useRankingEvents";
import { RANKING_SSE_QUERY_KEYS } from "../../../constants/rankingEventQueryKeys";
import { BrazilDate } from "../../../utils/BrazilDate";
import { filaEntregaService } from "../../../services/filaEntregaService";
import { useEntregas, useUpdateEntrega, useFinalizarEntrega } from "../../../hooks/useEntrega";
import {
  EntregadorEmEntrega,
  EntregadorNaFila,
} from "../../Roterista/types";
import {
  LoteSugerido,
  LotesRoteirizacaoResponse,
  roteirizacaoService,
} from "../../../services/roteirizacaoService";

interface RoteristaAutomaticoContextType {
  lojaId: number | undefined;
  lojas: Loja[];
  selectedLojaId: number | undefined;
  setSelectedLojaId: (id: number | undefined) => void;
  showLojaSelector: boolean;
  isLoading: boolean;
  lotesData: LotesRoteirizacaoResponse | undefined;
  selectedLoteId: string | null;
  setSelectedLoteId: (id: string | null) => void;
  selectedLote: LoteSugerido | null;
  entregadoresNaFila: EntregadorNaFila[];
  entregadoresEmEntrega: EntregadorEmEntrega[];
  proximoChamado: EntregadorNaFila | null;
  enderecos: number | "";
  lockedDisplayIds: number[];
  isChamando: boolean;
  isConfirmandoSaida: boolean;
  usaIntegracaoPedidos: boolean;
  handleChamarProximo: () => Promise<void>;
  handleChamarEntregador: (entregadorId: number) => Promise<void>;
  handleConfirmarSaida: (displayIds: number[]) => Promise<void>;
  handleMoverParaFim: () => Promise<void>;
  handleChamarNovamente: () => Promise<void>;
  handleCloseModal: () => void;
  atualizarPedidosEntrega: (
    entregaId: number,
    pedidosDisplayIds: number[]
  ) => Promise<void>;
  handleFinalizarEntrega: (entregaId: number) => Promise<void>;
  finalizandoEntregaId: number | null;
  refreshLive: () => void;
}

const RoteristaAutomaticoContext = createContext<
  RoteristaAutomaticoContextType | undefined
>(undefined);

export const RoteristaAutomaticoProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const podeVerTodasLojas = canVerTodasLojasUser(user);
  const [selectedLojaId, setSelectedLojaId] = useState<number | undefined>(
    () => user?.lojaId
  );
  const [selectedLoteId, setSelectedLoteId] = useState<string | null>(null);
  const [proximoChamado, setProximoChamado] = useState<EntregadorNaFila | null>(
    null
  );
  const [enderecos, setEnderecos] = useState<number | "">("");
  const [lockedDisplayIds, setLockedDisplayIds] = useState<number[]>([]);
  const [isChamando, setIsChamando] = useState(false);
  const [isConfirmandoSaida, setIsConfirmandoSaida] = useState(false);
  const confirmandoSaidaRef = useRef(false);
  const [finalizandoEntregaId, setFinalizandoEntregaId] = useState<
    number | null
  >(null);
  const finalizandoEntregaRef = useRef(false);
  const [liveUpdatesEnabled, setLiveUpdatesEnabled] = useState(false);
  const [entregaSseAvailable, setEntregaSseAvailable] = useState(true);

  const effectiveLojaId = podeVerTodasLojas
    ? (selectedLojaId ?? user?.lojaId)
    : user?.lojaId;
  const lojaId = effectiveLojaId;

  const { data: lojas = [] } = useQuery({
    queryKey: ["lojas"],
    queryFn: () => lojaService.getLojas(),
  });

  const { data: lojaAtual } = useLojaById(lojaId);

  const { data: rankingData, isLoading: isLoadingRanking } =
    useRankingDiaEntrega(
      {
        lojaId: lojaId || undefined,
      },
      { refetchInterval: entregaSseAvailable ? 15_000 : 8_000 }
    );

  const {
    data: lotesData,
    isLoading: isLoadingLotes,
  } = useQuery({
    queryKey: ["roteirizacao-lotes", lojaId],
    queryFn: () => roteirizacaoService.getLotes(lojaId!),
    enabled: Boolean(lojaId),
    // Segurança se o SSE bufferizar: mantém fila/lotes frescos
    refetchInterval: entregaSseAvailable ? 20_000 : 10_000,
  });

  const { data: entregasData } = useEntregas({
    lojaId: lojaId || undefined,
    status: "ativo",
  });

  const createEntregaMutation = useCreateEntrega();
  const updateFilaMutation = useUpdateFilaEntregaStatus();
  const updateEntregaMutation = useUpdateEntrega();
  const finalizarEntregaMutation = useFinalizarEntrega();

  useEffect(() => {
    const timer = window.setTimeout(() => setLiveUpdatesEnabled(true), 1500);
    return () => window.clearTimeout(timer);
  }, []);

  const invalidateLive = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["roteirizacao-lotes"] });
    void queryClient.invalidateQueries({ queryKey: ["rankingDiaEntrega"] });
    void queryClient.invalidateQueries({ queryKey: ["entregas"] });
    void queryClient.invalidateQueries({ queryKey: ["aguardando-entrega"] });
    void queryClient.invalidateQueries({ queryKey: ["filaEntregas"] });
  }, [queryClient]);

  useRankingEvents("entrega", lojaId || null, {
    queryKeys: RANKING_SSE_QUERY_KEYS.roteristaAutomatico,
    enabled: liveUpdatesEnabled,
    onAvailabilityChange: setEntregaSseAvailable,
  });

  useEffect(() => {
    if (entregaSseAvailable) return;
    invalidateLive();
    const interval = window.setInterval(invalidateLive, 10_000);
    return () => window.clearInterval(interval);
  }, [entregaSseAvailable, invalidateLive]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") invalidateLive();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [invalidateLive]);

  const rankingPorEntregador = useMemo(() => {
    const map = new Map<number, number>();
    rankingData?.ranking?.forEach((r) => {
      map.set(r.usuarioId, r.quantidadeEntregas);
    });
    return map;
  }, [rankingData?.ranking]);

  const entregadoresNaFila = useMemo(() => {
    if (!rankingData?.aguardando) return [];
    return rankingData.aguardando
      .map((item) => {
        const parts = item.nome.split(" ");
        return {
          id: item.id,
          entregadorId: item.entregadorId,
          nome: parts[0] || "",
          sobrenome: parts.slice(1).join(" ") || "",
          posicao: 0,
          filaId: item.id,
          dataEntrada:
            typeof item.dataEntrada === "string"
              ? item.dataEntrada
              : new Date(item.dataEntrada).toISOString(),
          quantidadeEntregasFinalizadas:
            rankingPorEntregador.get(item.entregadorId) ?? 0,
        };
      })
      .sort(
        (a, b) =>
          new Date(a.dataEntrada).getTime() - new Date(b.dataEntrada).getTime()
      )
      .map((item, index) => ({ ...item, posicao: index + 1 }));
  }, [rankingData, rankingPorEntregador]);

  const entregadoresEmEntrega = useMemo(() => {
    const emEntrega: EntregadorEmEntrega[] = [];
    const entregadoresUnicos = new Set<number>();

    if (entregasData?.data) {
      entregasData.data.forEach((entrega) => {
        if (
          entrega.status === "ativo" &&
          !entrega.dataFim &&
          entrega.entregador &&
          !entregadoresUnicos.has(entrega.entregador.id)
        ) {
          entregadoresUnicos.add(entrega.entregador.id);
          const nomeCompleto = entrega.entregador.nome.split(" ");
          emEntrega.push({
            id: entrega.id,
            entregadorId: entrega.entregador.id,
            nome: nomeCompleto[0] || "",
            sobrenome: nomeCompleto.slice(1).join(" ") || "",
            quantidadeEntregasFinalizadas:
              rankingPorEntregador.get(entrega.entregador.id) ?? 0,
            quantidadeEnderecos: entrega.quantidadeEnderecos ?? 1,
            pedidosDisplayIds: entrega.pedidosDisplayIds ?? undefined,
          });
        }
      });
    }

    return emEntrega;
  }, [entregasData, rankingPorEntregador]);

  useEffect(() => {
    const liberados =
      lotesData?.lotes.filter((l) => l.liberadoParaSaida !== false) ?? [];
    if (!liberados.length) {
      setSelectedLoteId(null);
      return;
    }
    if (
      !selectedLoteId ||
      !liberados.some((l) => l.id === selectedLoteId)
    ) {
      setSelectedLoteId(liberados[0].id);
    }
  }, [lotesData, selectedLoteId]);

  const selectedLote =
    lotesData?.lotes.find((l) => l.id === selectedLoteId) ?? null;

  const usaIntegracaoPedidos =
    lojaAtual?.usaIntegracaoPedidos ??
    user?.loja?.usaIntegracaoPedidos ??
    false;

  const abrirChamada = (entregador: EntregadorNaFila) => {
    if (!selectedLote) {
      toast.error("Selecione um lote sugerido antes de chamar.");
      return false;
    }
    if (selectedLote.liberadoParaSaida === false) {
      toast.error(
        selectedLote.motivoAguardando ||
          "Lote ainda aguardando tempo de casamento."
      );
      return false;
    }
    const ids = selectedLote.pedidos.map((p) => p.displayId);
    setLockedDisplayIds(ids);
    setEnderecos(ids.length);
    setProximoChamado(entregador);
    return true;
  };

  const handleChamarProximo = async () => {
    if (entregadoresNaFila.length === 0) {
      toast.error("Nenhum entregador na fila.");
      return;
    }
    const proximo = entregadoresNaFila[0];
    const filaId = proximo.filaId ?? proximo.id;
    if (filaId == null) {
      toast.error("Erro: ID da fila não encontrado.");
      return;
    }
    if (!selectedLote) {
      toast.error("Selecione um lote sugerido antes de chamar.");
      return;
    }

    setIsChamando(true);
    try {
      await filaEntregaService.chamarEntregador(filaId);
      abrirChamada(proximo);
    } catch (error) {
      console.error(error);
      toast.error(
        "Não foi possível emitir o alerta na tela de entregas. Tente novamente."
      );
    } finally {
      setIsChamando(false);
    }
  };

  const handleChamarEntregador = async (entregadorId: number) => {
    const entregador = entregadoresNaFila.find(
      (e) => e.entregadorId === entregadorId
    );
    if (!entregador) return;
    const filaId = entregador.filaId ?? entregador.id;
    if (!selectedLote) {
      toast.error("Selecione um lote sugerido antes de chamar.");
      return;
    }

    setIsChamando(true);
    try {
      await filaEntregaService.chamarEntregador(filaId);
      abrirChamada(entregador);
    } catch (error) {
      console.error(error);
      toast.error(
        "Não foi possível emitir o alerta na tela de entregas. Tente novamente."
      );
    } finally {
      setIsChamando(false);
    }
  };

  const handleConfirmarSaida = async (displayIds: number[]) => {
    if (confirmandoSaidaRef.current || isConfirmandoSaida) return;

    const ids =
      lockedDisplayIds.length > 0 ? lockedDisplayIds : displayIds;
    const qty =
      typeof enderecos === "number" ? enderecos : lockedDisplayIds.length;

    if (!qty || qty < 1 || qty > 3) {
      toast.error("Quantidade de endereços inválida.");
      return;
    }
    if (!lockedDisplayIds.length) {
      toast.error("Lote automático sem comandas definidas pelo sistema.");
      return;
    }
    if (ids.length !== qty) {
      toast.error("As comandas do lote não batem com a quantidade de endereços.");
      return;
    }
    if (!proximoChamado || !lojaId) {
      toast.error("Erro ao confirmar saída");
      return;
    }

    confirmandoSaidaRef.current = true;
    setIsConfirmandoSaida(true);
    try {
      await createEntregaMutation.mutateAsync({
        lojaId,
        entregadorId: proximoChamado.entregadorId,
        quantidadeEnderecos: qty,
        pedidosDisplayIds: ids,
      });

      await updateFilaMutation.mutateAsync({
        id: proximoChamado.filaId,
        data: { status: "em_andamento" },
      });

      toast.success(
        `Saída confirmada! Pedidos ${ids.map((id) => `#${id}`).join(", ")}`
      );
      setProximoChamado(null);
      setEnderecos("");
      setLockedDisplayIds([]);
      invalidateLive();
    } catch (error) {
      console.error(error);
      toast.error(
        error instanceof Error ? error.message : "Erro ao confirmar saída"
      );
    } finally {
      confirmandoSaidaRef.current = false;
      setIsConfirmandoSaida(false);
    }
  };

  const handleMoverParaFim = async () => {
    if (!proximoChamado) return;
    try {
      await updateFilaMutation.mutateAsync({
        id: proximoChamado.filaId,
        data: {
          status: "aguardando",
          dataFila: BrazilDate.now().toISOString(),
        },
      });
      toast.success("Entregador movido para o fim da fila");
      setProximoChamado(null);
      setEnderecos("");
      setLockedDisplayIds([]);
      invalidateLive();
    } catch (error) {
      console.error(error);
      toast.error("Erro ao mover entregador");
    }
  };

  const handleChamarNovamente = async () => {
    if (!proximoChamado) return;
    const filaId = proximoChamado.filaId ?? proximoChamado.id;
    if (filaId == null) return;
    try {
      await filaEntregaService.chamarEntregador(filaId);
      toast.success("Chamada emitida novamente na tela de entregas.");
    } catch (error) {
      console.error(error);
      toast.error(
        "Não foi possível emitir o alerta novamente. Tente novamente."
      );
    }
  };

  const handleCloseModal = () => {
    setProximoChamado(null);
    setEnderecos("");
    setLockedDisplayIds([]);
  };

  const atualizarPedidosEntrega = async (
    entregaId: number,
    pedidosDisplayIds: number[]
  ) => {
    await updateEntregaMutation.mutateAsync({
      id: entregaId,
      data: {
        pedidosDisplayIds,
        quantidadeEnderecos:
          pedidosDisplayIds.length > 0 ? pedidosDisplayIds.length : 1,
      },
    });
    toast.success("Números do pedido atualizados.");
  };

  const handleFinalizarEntrega = async (entregaId: number) => {
    if (finalizandoEntregaRef.current || finalizandoEntregaId != null) return;

    finalizandoEntregaRef.current = true;
    setFinalizandoEntregaId(entregaId);
    try {
      await finalizarEntregaMutation.mutateAsync(entregaId);
      invalidateLive();
      toast.success(
        "Entrega finalizada! Entregador volta para a fila aguardando."
      );
    } catch (error) {
      const message =
        error instanceof Error ? error.message : String(error);
      if (message.includes("já foi finalizada")) {
        invalidateLive();
        toast.success("Entrega já estava finalizada.");
      } else {
        console.error(error);
        toast.error(
          "Erro ao finalizar entrega: " +
            (error instanceof Error ? error.message : "Erro desconhecido")
        );
      }
    } finally {
      finalizandoEntregaRef.current = false;
      setFinalizandoEntregaId(null);
    }
  };

  if (!podeVerTodasLojas && !lojaId) {
    return (
      <div className="flex h-screen items-center justify-center">
        <p className="text-neutral-500">Erro: Loja não identificada</p>
      </div>
    );
  }

  return (
    <RoteristaAutomaticoContext.Provider
      value={{
        lojaId,
        lojas,
        selectedLojaId,
        setSelectedLojaId,
        showLojaSelector: podeVerTodasLojas,
        isLoading: isLoadingRanking || isLoadingLotes,
        lotesData,
        selectedLoteId,
        setSelectedLoteId,
        selectedLote,
        entregadoresNaFila,
        entregadoresEmEntrega,
        proximoChamado,
        enderecos,
        lockedDisplayIds,
        isChamando,
        isConfirmandoSaida,
        usaIntegracaoPedidos,
        handleChamarProximo,
        handleChamarEntregador,
        handleConfirmarSaida,
        handleMoverParaFim,
        handleChamarNovamente,
        handleCloseModal,
        atualizarPedidosEntrega,
        handleFinalizarEntrega,
        finalizandoEntregaId,
        refreshLive: invalidateLive,
      }}
    >
      {children}
    </RoteristaAutomaticoContext.Provider>
  );
};

export const useRoteristaAutomatico = () => {
  const ctx = useContext(RoteristaAutomaticoContext);
  if (!ctx) {
    throw new Error(
      "useRoteristaAutomatico must be used within RoteristaAutomaticoProvider"
    );
  }
  return ctx;
};
